// src/pages/BookmarksPage.jsx
import React, { useEffect, useState } from "react";
import { fetchBookmarkGroups, fetchBookmarksForGroup } from "../api";
import { useNavigate } from "react-router-dom";

/**
 * Shows grid/list of bookmark groups with counts.
 * Clicking a group navigates to /bookmarks/:groupId
 */
export default function BookmarksPage({ user }) {
  const uid = user?.uid;
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({}); // groupId -> count (optional, filled lazily)

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const g = await fetchBookmarkGroups(uid);
        if (!mounted) return;
        setGroups(g || []);
      } catch (e) {
        console.error("Failed to load bookmark groups", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [uid]);

  // Optionally compute counts lazily: fetch number of bookmarks for each group in background
  useEffect(() => {
    if (!uid || !groups || groups.length === 0) return;
    let mounted = true;
    (async () => {
      try {
        const newCounts = {};
        await Promise.all(
          groups.map(async (g) => {
            try {
              const b = await fetchBookmarksForGroup(uid, g.id);
              if (!mounted) return;
              newCounts[g.id] = (b || []).length;
            } catch (e) {
              newCounts[g.id] = 0;
            }
          })
        );
        if (!mounted) return;
        setCounts(newCounts);
      } catch (e) {
        // ignore
      }
    })();
    return () => (mounted = false);
  }, [uid, groups]);

  if (!user) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl">Please sign in to view your bookmarks</h2>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Your Bookmark Groups</h1>
        <button
          onClick={() => navigate("/")}
          className="px-3 py-1 border rounded text-sm"
        >
          Back
        </button>
      </div>

      {loading ? (
        <div>Loading groups…</div>
      ) : groups.length === 0 ? (
        <div className="text-gray-500">No bookmark groups found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {groups.map((g) => (
            <div
              key={g.id}
              onClick={() => navigate(`/bookmarks/${g.id}`)}
              className="cursor-pointer p-4 border rounded shadow-sm hover:shadow-md transition"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-lg">{g.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Created:{" "}
                    {g.createdAt
                      ? new Date(
                          g.createdAt.seconds
                            ? g.createdAt.seconds * 1000
                            : g.createdAt
                        ).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </div>
                </div>
                <div className="text-sm text-gray-700 font-medium">
                  {counts[g.id] != null ? counts[g.id] : "—"} Qs
                </div>
              </div>

              <div className="mt-3 text-sm text-gray-600">
                Click to open and practice the bookmarked questions.
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
